import { motion } from 'framer-motion';
import { Users, Target, Award, Rocket, Heart, Coffee, CheckCircle, Globe, Zap, Shield, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';

const milestones = [
  {
    year: '2020',
    title: 'Company Founded',
    description: 'Started with a vision to transform digital marketing'
  },
  {
    year: '2021',
    title: 'Global Expansion',
    description: 'Expanded services to international markets'
  },
  {
    year: '2023',
    title: 'AI Integration',
    description: 'Pioneered AI-driven marketing solutions'
  },
  {
    year: '2025',
    title: 'Industry Leader',
    description: 'Recognized as a leading digital transformation partner'
  }
];

const values = [
  {
    icon: Heart,
    title: 'Client-First Approach',
    description: "We prioritize our clients' success above all else, ensuring every solution is tailored to their unique needs and goals.",
    color: 'bg-red-50 text-red-600'
  },
  {
    icon: Target,
    title: 'Results-Driven',
    description: 'Our strategies are built on data-driven insights, focusing on delivering measurable outcomes that matter.',
    color: 'bg-blue-50 text-blue-600'
  },
  {
    icon: Rocket,
    title: 'Innovation',
    description: 'We stay at the forefront of technology, constantly evolving our solutions to meet future challenges.',
    color: 'bg-purple-50 text-purple-600'
  },
  {
    icon: Shield,
    title: 'Trust & Security',
    description: 'We maintain the highest standards of security and transparency in all our operations.',
    color: 'bg-green-50 text-green-600'
  }
];

const missionPoints = [
  {
    title: 'Digital Innovation',
    description: 'Pioneering cutting-edge solutions that reshape industries and drive growth',
    icon: Zap
  },
  {
    title: 'Measurable Results',
    description: 'Delivering data-driven strategies that produce tangible business outcomes',
    icon: Target
  },
  {
    title: 'Lasting Partnerships',
    description: 'Building strong, enduring relationships based on trust and mutual success',
    icon: Users
  },
  {
    title: 'Client Success',
    description: 'Ensuring our clients achieve and exceed their business objectives',
    icon: Award
  }
];

export default function About() {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('consultation');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <header className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1920&q=80')] bg-cover bg-center opacity-10" />
          <div className="absolute inset-0 bg-blue-900/50" />
          
          {/* Animated circles */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 8,
                delay: i * 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute rounded-full bg-white/10 backdrop-blur-3xl"
              style={{
                width: `${300 + i * 200}px`,
                height: `${300 + i * 200}px`,
                left: `${30 + i * 10}%`,
                top: `${20 + i * 15}%`,
                transform: 'translate(-50%, -50%)'
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-8"
            >
              <Globe className="w-10 h-10 text-white" />
            </motion.div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-blue-100 to-white">
              Transforming Digital Futures
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-blue-100/90">
              Since 2020, we've been at the forefront of digital innovation, helping businesses thrive in the digital age
            </p>
            <Button 
              variant="outline" 
              size="lg" 
              onClick={handleContactClick}
              className="border-white text-white hover:bg-white/10 inline-flex items-center gap-2 px-8"
            >
              <MessageSquare className="w-5 h-5" />
              Schedule Consultation
            </Button>
          </motion.div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
      </header>

      {/* Timeline Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A timeline of our growth and achievements
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-200"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className={`flex ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-lg">
                      <div className="text-3xl font-bold text-blue-600 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="relative flex items-center justify-center">
                    <div className="w-8 h-8 bg-blue-600 rounded-full z-10"></div>
                  </div>
                  <div className="flex-1"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The core principles that drive our success and shape our culture
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg p-8 text-center transform hover:scale-105 transition-transform duration-300"
              >
                <div className={`w-16 h-16 ${value.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <value.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}